import type { ValueOf } from 'type-fest'

import type { CurriculumApi } from '~/@entities/Curriculum/api'
import type { Entity as CurriculumEntity } from '~/@entities/Curriculum/dto'

import type { SchoolType } from '../SchoolSystem/schema'

export const SCHOOL_TYPE: ({
  label: '초' | '중' | '고'
  value: SchoolType
} & Pick<CurriculumEntity.CurriculumCoreItem, 'subscribeStatus'>)[] = [
  { label: '초', value: 'ELEMENTARY', subscribeStatus: 'SUBSCRIBED' },
  { label: '중', value: 'MIDDLE', subscribeStatus: 'SUBSCRIBED' },
  { label: '고', value: 'HIGH', subscribeStatus: 'SUBSCRIBED' },
] as const

export const SCHOOL_TYPE_EXPANDED: ({
  label: '초' | '중' | '고' | '전체'
  value: SchoolType | 'ALL'
} & Pick<CurriculumEntity.CurriculumCoreItem, 'subscribeStatus'>)[] = [
  { label: '전체', value: 'ALL', subscribeStatus: 'SUBSCRIBED' },
  ...SCHOOL_TYPE,
] as const

const ELEMENTARY_SCHOOL_CURRICULUM_KEYS = {} satisfies Record<string, CurriculumApi.TrieKey>

const MIDDLE_SCHOOL_CURRICULUM_KEYS = {
  CURRICULUM_KEY_중학교_1학년_22개정_과학: '1.2.3.4',
  CURRICULUM_KEY_중학교_2학년_22개정_과학: '1.2.3.7',
  CURRICULUM_KEY_중학교_3학년_15개정_과학: '1.10.11.12',
  CURRICULUM_KEY_중학교_1학년_1학기_22개정_과학: '1.2.3.4.5',
  CURRICULUM_KEY_중학교_1학년_2학기_22개정_과학: '1.2.3.4.6',
  CURRICULUM_KEY_중학교_2학년_1학기_22개정_과학: '1.2.3.7.8',
  CURRICULUM_KEY_중학교_2학년_2학기_22개정_과학: '1.2.3.7.9',
  CURRICULUM_KEY_중학교_3학년_1학기_15개정_과학: '1.10.11.12.13',
  CURRICULUM_KEY_중학교_3학년_2학기_15개정_과학: '1.10.11.12.14',
} satisfies Record<string, CurriculumApi.TrieKey>

const HIGH_SCHOOL_CURRICULUM_KEYS = {
  CURRICULUM_KEY_고등학교_통합과학1_22개정: '1.2.15.16',
  CURRICULUM_KEY_고등학교_통합과학2_22개정: '1.2.15.17',
} satisfies Record<string, CurriculumApi.TrieKey>

export const NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS = {
  CURRICULUM_KEY_고등학교_교육과정_외: '1.2.7.48',
  CURRICULUM_KEY_고등학교_구_교육과정: '1.2.7.49',
  CURRICULUM_KEY_고등학교_구_교육과정_22개정: '1.4.4147.4182',
  CURRICULUM_KEY_고등학교_교육과정_외_22개정: '1.4.4147.4183',
} satisfies Record<string, CurriculumApi.TrieKey>

export const CURRICULUM_KEYS = {
  ...ELEMENTARY_SCHOOL_CURRICULUM_KEYS,

  ...MIDDLE_SCHOOL_CURRICULUM_KEYS,

  ...HIGH_SCHOOL_CURRICULUM_KEYS,
} satisfies Record<string, CurriculumApi.TrieKey>

// 1차 오픈 키(= 사전 오픈 = 풀오픈 이전)
export const PRE_OPEN_CURRICULUM_KEYS = [] satisfies readonly CurriculumApi.TrieKey[]

export const DEFAULT_HIGH_SCHOOL_CURRICULUM_KEY =
  HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_통합과학1_22개정

export const SUBJECT_HIGH_KO = {
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_통합과학1_22개정]: '통합과학1',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_통합과학2_22개정]: '통합과학2',
} as const satisfies Record<
  ValueOf<typeof HIGH_SCHOOL_CURRICULUM_KEYS>,
  Exclude<CurriculumApi.HighSchoolSubject, '(구) 교육과정' | '교육과정 외'>
>

export const ALL_SUBJECT_HIGH_KO_BASED_TRIE_KEY = {
  ...SUBJECT_HIGH_KO,
} as const satisfies Record<
  | ValueOf<typeof HIGH_SCHOOL_CURRICULUM_KEYS>
  | ValueOf<typeof NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS>,
  CurriculumApi.HighSchoolSubject
>

export const SUBJECT_HIGH_SHORT = {
  ...Object.values(ALL_SUBJECT_HIGH_KO_BASED_TRIE_KEY).reduce(
    (acc, v) => {
      if (v === '통합과학1' || v === '통합과학2') {
        const short = (() => {
          switch (v) {
            case '통합과학1':
              return '통합과학1'
            case '통합과학2':
              return '통합과학2'
            default:
              return v
          }
        })()
        return { ...acc, [v]: short }
      }
      return { ...acc, [v]: v }
    },
    {} as Record<
      CurriculumApi.HighSchoolSubject,
      | Exclude<CurriculumApi.HighSchoolSubject, '통합과학1' | '통합과학2'>
      | '통합과학1'
      | '통합과학2'
    >,
  ),
} as const

export const OUT_OF_CURRICULUM_TYPE = {
  교육과정내: 'CURRICULUM',
  교육과정외: 'EXTRA_CURRICULUM',
  이전교육과정: 'PREVIOUS_GRADE',
} as const

export const CURRICULUM_CONCEPT_TYPE = {
  소단원: 'LITTLE_CHAPTER',
  중단원: 'MIDDLE_CHAPTER',
} as const satisfies Record<
  '소단원' | '중단원',
  Extract<CurriculumApi.CurriculumType, 'LITTLE_CHAPTER' | 'MIDDLE_CHAPTER'>
>

/**
 * 개정
 */
export const REVISION_KO = {
  CURRICULUM_15: '15개정',
  CURRICULUM_22: '22개정',
} as const satisfies Record<CurriculumApi.Revision, '15개정' | '22개정'>

export const REVISIONS = [
  {
    label: '15개정',
    value: 'CURRICULUM_15',
  },
  {
    label: '22개정',
    value: 'CURRICULUM_22',
  },
] as const satisfies readonly {
  readonly label: ValueOf<typeof REVISION_KO>
  readonly value: CurriculumApi.Revision
}[]

/**
 * 단원 트리 node의 깊이(depth) 값
 */
export const CURRICULUM_DEPTH = {
  학년학기: 0,
  대단원: 1,
  중단원: 2,
  소단원: 3,
  유형: 4,
  주제: 5,
  세부: 6,
} as const
